// ═══════════════════════════════════════════════════════════════
//  micData.js
//  Funciones puras y datos estáticos — sin dependencias de React.
//  Importar en useMicSystem.js y donde se necesite.
// ═══════════════════════════════════════════════════════════════

// ─────────────────────────────────────────────────────────────
//  NORMALIZE
//  Limpia un string antes de comparar: minúsculas, sin puntuación
//  ni espacios extra. Llámala sobre TODOS los strings antes de
//  pasarlos a smartMatch.
// ─────────────────────────────────────────────────────────────
export function normalizeText(str = '') {
  return str
    .toLowerCase()
    .replace(/[.,/#!$%^&*;:{}=\-_`~()?¿!¡'"]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

// ─────────────────────────────────────────────────────────────
//  LEVENSHTEIN SIMILARITY  (0 = nada en común, 1 = idénticos)
// ─────────────────────────────────────────────────────────────
export function calcSimilarity(a, b) {
  if (a === b) return 1;
  const la = a.length, lb = b.length;
  if (!la || !lb) return 0;
  const dp = Array.from({ length: la + 1 }, (_, i) =>
    Array.from({ length: lb + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
  );
  for (let i = 1; i <= la; i++)
    for (let j = 1; j <= lb; j++)
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
  return 1 - dp[la][lb] / Math.max(la, lb);
}

// ─────────────────────────────────────────────────────────────
//  PHONETIC MAP
//  Cubre las variantes que el SR (en-US) devuelve para cada
//  palabra cuando la pronuncia un hispanohablante.
//
//  Claves normalizadas (minúsculas, sin puntuación).
//  Valores: array de transcripciones alternativas esperadas.
// ─────────────────────────────────────────────────────────────
export const PHONETIC_MAP = {
  // ── PRONOMBRES ───────────────────────────────────────────────
  i:            ['i', 'eye', 'ay', 'aye', 'ai', 'i am', 'im'],
  you:          ['you', 'u', 'yu', 'yoo', 'you are', 'ewe'],
  he:           ['he', 'hee', 'hi', 'hay', 'he is', 'hes'],
  she:          ['she', 'shee', 'shi', 'she is', 'shes'],
  it:           ['it', 'eat', 'et', 'id', 'it is', 'its'],
  we:           ['we', 'wee', 'whee', 'wi', 'we are', 'were'],
  they:         ['they', 'the', 'day', 'de', 'dei', 'they are'],
  me:           ['me', 'mi', 'mee'],
  him:          ['him', 'hymn', 'im', 'heem'],
  her:          ['her', 'here', 'hur', 'ir', 'err'],
  us:           ['us', 'uss', 'iz', 'uz'],
  them:         ['them', 'dem', 'em', 'therm'],

  // ── VERBOS CORTOS / DIFÍCILES ────────────────────────────────
  be:           ['be', 'bee', 'bi', 'b'],
  do:           ['do', 'doo', 'due', 'dew', 'to do'],
  go:           ['go', 'goo', 'joe'],
  run:          ['run', 'ran', 'rn', 'ron'],
  read:         ['read', 'reed', 'red', 'rid'],
  write:        ['write', 'right', 'wright', 'rite', 'ride'],
  know:         ['know', 'no', 'now', 'noh', 'noe'],
  see:          ['see', 'sea', 'si', 'say'],
  hear:         ['hear', 'here', 'hair', 'heer', 'year'],
  learn:        ['learn', 'lern', 'burn', 'lurn'],
  speak:        ['speak', 'spi', 'spik', 'espe', 'espeak', 'spec'],
  think:        ['think', 'sink', 'fink', 'ting', 'thing', 'tink'],
  build:        ['build', 'bild', 'bill', 'built'],
  break:        ['break', 'brake', 'brek', 'braik'],
  choose:       ['choose', 'chews', 'chose', 'juice', 'chuse'],
  fall:         ['fall', 'fool', 'faul', 'fol'],
  grow:         ['grow', 'groh', 'gro', 'crow'],
  hold:         ['hold', 'old', 'jold', 'holt'],
  lead:         ['lead', 'led', 'leed', 'lid'],
  meet:         ['meet', 'mit', 'meat', 'met', 'feet'],
  pay:          ['pay', 'pei', 'pie', 'bay'],
  sell:         ['sell', 'sel', 'cel', 'spell', 'shell'],
  send:         ['send', 'sent', 'sand', 'end'],
  show:         ['show', 'sho', 'chow', 'joe', 'so'],
  spend:        ['spend', 'expend', 'spen'],
  stand:        ['stand', 'tan', 'san', 'estan'],
  win:          ['win', 'when', 'wen', 'vim', 'wing'],
  work:         ['work', 'walk', 'wore', 'wok', 'word'],
  achieve:      ['achieve', 'a chive', 'achive', 'achiev', 'achieved'],
  admit:        ['admit', 'omit', 'admet', 'submit'],
  agree:        ['agree', 'a gree', 'agreed', 'agri'],
  forget:       ['forget', 'for get', 'forgot', 'forguet'],
  remember:     ['remember', 'rimember', 'remmember'],
  teach:        ['teach', 'tich', 'teech', 'teeth'],
  understand:   ['understand', 'under stand', 'anderstand', 'inderstand', 'understood'],
  listen:       ['listen', 'lessen', 'lisin'],

  // ── SUSTANTIVOS ──────────────────────────────────────────────
  knowledge:    ['knowledge', 'nowledge', 'nolej', 'nowlege', 'college'],
  strength:     ['strength', 'strenth', 'strenk', 'strenght', 'strong'],
  friend:       ['friend', 'frend', 'fren', 'trend'],
  brother:      ['brother', 'broder', 'brather'],
  daughter:     ['daughter', 'doter', 'dauther', 'dotter'],
  neighbor:     ['neighbor', 'naybor', 'naber', 'neighbour'],
  journey:      ['journey', 'chorney', 'jurney', 'jorney'],
  challenge:    ['challenge', 'chalenge', 'chalenche', 'chelenge'],
  opportunity:  ['opportunity', 'oportunity', 'oportunit'],
  environment:  ['environment', 'invironment', 'enviroment'],
  government:   ['government', 'gobernment', 'goverment', 'guverment'],
  technology:   ['technology', 'tecnology', 'teknolohy'],
  decision:     ['decision', 'desicion', 'dicision', 'decition'],
  experience:   ['experience', 'experiencia', 'experiens', 'expirence'],
  solution:     ['solution', 'solucion', 'solushon', 'soluton'],
  purpose:      ['purpose', 'porpose', 'perpus', 'purpos'],
  failure:      ['failure', 'failyer', 'failer', 'failur'],
  success:      ['success', 'succes', 'suxes', 'sucess'],
  building:     ['building', 'bilding', 'bildeen', 'bielding'],
  freedom:      ['freedom', 'freedon', 'frido', 'fridom'],
  customer:     ['customer', 'costumer', 'custoumer'],
  question:     ['question', 'cuestion', 'cwestion', 'queshion'],
  answer:       ['answer', 'anser', 'ancer', 'unser'],
  idea:         ['idea', 'aidea', 'idear', 'ideal'],

  // ── ADJETIVOS ────────────────────────────────────────────────
  beautiful:    ['beautiful', 'beutiful', 'beautifull', 'byutiful', 'butiful'],
  dangerous:    ['dangerous', 'dengerous', 'dangereous', 'dangerus'],
  excellent:    ['excellent', 'excelent', 'excelente', 'exsellent'],
  ancient:      ['ancient', 'ansient', 'eincent'],
  brave:        ['brave', 'braiv', 'breiv'],
  clever:       ['clever', 'cleber', 'klever', 'clebber'],
  delicious:    ['delicious', 'delicioso', 'delicius', 'delishus'],
  enormous:     ['enormous', 'enourmous', 'enormus', 'inormous'],
  famous:       ['famous', 'famus', 'faimous', 'feimus'],
  generous:     ['generous', 'generus', 'henerous', 'yenerous'],
  honest:       ['honest', 'onest', 'onist', 'honist'],
  important:    ['important', 'inportant', 'importan', 'importent'],
  jealous:      ['jealous', 'jelous', 'yellous', 'jelos'],
  lazy:         ['lazy', 'laisy', 'leisy'],
  nervous:      ['nervous', 'nerbous', 'nerbus', 'nervios'],
  obvious:      ['obvious', 'obious', 'obvius', 'obviuos'],
  patient:      ['patient', 'paishent', 'peishent', 'paciente'],
  quiet:        ['quiet', 'cwiet', 'cuiet', 'quied', 'quite'],
  reliable:     ['reliable', 'reliabel', 'relayble'],
  serious:      ['serious', 'serius', 'cerius', 'serios'],
  stubborn:     ['stubborn', 'stuborn', 'estaborn', 'stubon'],
  talented:     ['talented', 'talentet', 'talentid'],
  unique:       ['unique', 'unic', 'yunik', 'uneek'],
  wealthy:      ['wealthy', 'welthy', 'weldhy'],
  ambitious:    ['ambitious', 'ambicioso', 'ambishus', 'ombitious'],

  // ── ADVERBIOS ────────────────────────────────────────────────
  quickly:      ['quickly', 'quikly', 'quicky', 'kwikly'],
  suddenly:     ['suddenly', 'sudenly', 'suddenli', 'sodenly'],
  carefully:    ['carefully', 'carfully', 'cearfully', 'carefuly'],
  hardly:       ['hardly', 'ardly', 'hardli', 'jardle'],
  barely:       ['barely', 'bairly', 'bearli', 'berly'],
  frequently:   ['frequently', 'frecuently', 'frekuently'],
  lately:       ['lately', 'leitly', 'laitly', 'lateli'],
  previously:   ['previously', 'previusly', 'previosli'],
  definitely:   ['definitely', 'definately', 'definitly', 'definetly'],
  absolutely:   ['absolutely', 'absolutly', 'absoltely', 'absulutly'],
  generally:    ['generally', 'generaly', 'genrally', 'jenerally'],
  obviously:    ['obviously', 'obviusly', 'obviosly', 'obvisly'],
  probably:     ['probably', 'probaly', 'probbably', 'probabli'],
  actually:     ['actually', 'acutally', 'actualy', 'ackshually'],
  immediately:  ['immediately', 'imediatly', 'inmediatly', 'imediately'],

  // ── PHRASAL VERBS ────────────────────────────────────────────
  'give up':         ['give up', 'give it up', 'give', 'gives up', 'given up'],
  'look up':         ['look up', 'look it up', 'look', 'lookup'],
  'put off':         ['put off', 'put it off', 'put of', 'putoff'],
  'turn down':       ['turn down', 'turned down', 'turn it down'],
  'take off':        ['take off', 'took off', 'take it off', 'takeoff'],
  'run out of':      ['run out of', 'run out', 'ran out'],
  'get away':        ['get away', 'getaway', 'got away'],
  'bring up':        ['bring up', 'brought up', 'bring it up'],
  'carry out':       ['carry out', 'carry it out', 'carried out'],
  'come across':     ['come across', 'came across', 'come a cross'],
  'deal with':       ['deal with', 'dealt with', 'deal width', 'dill with'],
  'end up':          ['end up', 'ended up'],
  'figure out':      ['figure out', 'figured out', 'figure it out', 'figure'],
  'go through':      ['go through', 'gone through', 'go thru'],
  'hold on':         ['hold on', 'hold it', 'holdon'],
  'keep up with':    ['keep up with', 'keep up', 'keeping up with'],
  'look forward to': ['look forward to', 'look forward', 'looking forward to'],
  'make up':         ['make up', 'makeup', 'made up'],
  'point out':       ['point out', 'pointed out', 'point it out'],
  'set up':          ['set up', 'setup', 'set it up'],

  // ── BUSINESS ─────────────────────────────────────────────────
  deadline:      ['deadline', 'dead line', 'dedline', 'deadlin'],
  budget:        ['budget', 'budyet', 'bujet', 'badget'],
  strategy:      ['strategy', 'estragy', 'estrategy', 'strategi'],
  negotiate:     ['negotiate', 'negociate', 'negoshiate'],
  implement:     ['implement', 'implementar', 'impliment', 'inplement'],
  revenue:       ['revenue', 'revenu', 'rebenue', 'revenou'],
  stakeholder:   ['stakeholder', 'stake holder', 'steak holder'],
  milestone:     ['milestone', 'mile stone', 'mailstone'],
  leverage:      ['leverage', 'leveraje', 'liverege', 'levarage'],
  outsource:     ['outsource', 'out source', 'outsors', 'autsource'],
  disruption:    ['disruption', 'disrupcion', 'desruption'],
  acquisition:   ['acquisition', 'adquisicion', 'acquisicion', 'acuisition'],
  benchmark:     ['benchmark', 'bench mark', 'benchmork'],
  scalable:      ['scalable', 'scalabol', 'skalabol', 'escalable'],
  collaborate:   ['collaborate', 'colaborate', 'colaberate'],
  initiative:    ['initiative', 'iniciative', 'initiatve', 'inisiative'],
  portfolio:     ['portfolio', 'portafolio', 'porfolio', 'portfilio'],
  logistics:     ['logistics', 'logistica', 'logistik'],
  transparency:  ['transparency', 'transparencia', 'transparncy'],
  innovation:    ['innovation', 'innovacion', 'inovation', 'innovasion'],

  // ── TRAVEL ───────────────────────────────────────────────────
  passport:        ['passport', 'pasport', 'passaport', 'pasaport'],
  departure:       ['departure', 'departur', 'diparture', 'departcher'],
  destination:     ['destination', 'destinacion', 'destinetion'],
  luggage:         ['luggage', 'lugaje', 'luguge', 'lugage'],
  customs:         ['customs', 'costums', 'custums'],
  itinerary:       ['itinerary', 'itinirery', 'itinerari', 'itenerari'],
  reservation:     ['reservation', 'reservacion', 'reservetion'],
  'boarding pass':  ['boarding pass', 'boarding', 'bordin pass', 'bording pass'],
  'exchange rate':  ['exchange rate', 'exchange', 'excange rate'],
  accommodation:   ['accommodation', 'acomodation', 'acomodacion'],
  turbulence:      ['turbulence', 'turbulencia', 'turbolence', 'turbulens'],
  souvenir:        ['souvenir', 'subenir', 'souvnir', 'suvenir'],
  currency:        ['currency', 'curency', 'corency', 'currenci'],
  landmark:        ['landmark', 'land mark', 'landmork'],
  shuttle:         ['shuttle', 'shuddle', 'shotle', 'shutle'],
};

// ─────────────────────────────────────────────────────────────
//  SMART MATCH — 7 capas en cascada
//
//  @param  heard   string del SR (puede ser frase larga)
//  @param  target  palabra/frase objetivo
//  @param  alts    variantes explícitas del vocab (campo `alts`)
//  @returns { match: bool, confidence: string, ...debug }
// ─────────────────────────────────────────────────────────────
export function smartMatch(heard, target, alts = []) {
  const h = normalizeText(heard);
  const t = normalizeText(target);
  const heardWords = h.split(' ');

  // Capa 0 — variantes explícitas del vocab (campo alts del objeto word)
  if (alts.length > 0) {
    const normAlts = alts.map(normalizeText);
    if (normAlts.some(a => h === a || heardWords.includes(a))) {
      return { match: true, confidence: 'explicit-alt' };
    }
  }

  // Capa 1 — coincidencia exacta
  if (h === t) return { match: true, confidence: 'exact' };

  // Capa 2 — target como palabra suelta dentro del transcript
  //   SR suele devolver "I am" cuando el user dice "I"
  if (heardWords.includes(t)) return { match: true, confidence: 'word-found' };

  // Capa 3 — mapa fonético (exact + suelta + contains para multi-word)
  const phonetics = (PHONETIC_MAP[t] || []).map(normalizeText);
  for (const v of phonetics) {
    if (h === v)                    return { match: true, confidence: 'phonetic-exact' };
    if (heardWords.includes(v))     return { match: true, confidence: 'phonetic-word'  };
    if (v.includes(' ') && h.includes(v)) return { match: true, confidence: 'phonetic-contains' };
  }

  // Capa 4 — similitud palabra a palabra (threshold 0.82)
  // Capa 4 — similitud palabra a palabra (AUMENTAMOS EXIGENCIA A 0.88)
  let bestWordSim = 0;
  let bestWord    = '';
  for (const w of heardWords) {
    const s = calcSimilarity(w, t);
    if (s > bestWordSim) { bestWordSim = s; bestWord = w; }
  }
  if (bestWordSim >= 0.88) return { match: true, confidence: 'word-similar' };

  // Capa 5 — threshold adaptativo (ADIÓS A LOS FALSOS POSITIVOS)
  // Antes perdonaba con 0.45. Ahora exige altísima precisión.
  const threshold = t.length <= 3 ? 0.75 : t.length <= 5 ? 0.80 : 0.85;
  const fullSim   = calcSimilarity(h, t);
  if (fullSim >= threshold) return { match: true, confidence: 'fuzzy' };

  // Capa 6 — transcript empieza con el target
  if (h.startsWith(t + ' ') || h === t) return { match: true, confidence: 'starts-with' };

  // Capa 7 — cross-phonetic: heard es variante de una palabra parecida al target
  for (const [canonical, variants] of Object.entries(PHONETIC_MAP)) {
    const nc = normalizeText(canonical);
    if (nc === t) continue;
    const normV = variants.map(normalizeText);
    if ((normV.includes(h) || normV.some(v => heardWords.includes(v)))
        && calcSimilarity(nc, t) >= 0.75) {
      return { match: true, confidence: 'cross-phonetic' };
    }
  }

  return { match: false, confidence: 'none', heardClean: h, bestWordSim, fullSim };
}